import { useEffect, useState, useCallback } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useYjsCollaboration } from "pages/modules/kb/helpers/YjsCollaborationContext";
import type { ActiveUser } from "shared/types/kb-collaboration";
import { Avatar, AvatarGroup } from "components";
import { PlusIcon, CheckCircleOutlinedIcon } from "components/icons";

export function ActiveUsersList() {
  const theme = useTheme();
  const { awareness } = useYjsCollaboration();
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  }, []);

  useEffect(() => {
    const updateUsers = () => {
      const states = Array.from(awareness.getStates().entries());
      const users: ActiveUser[] = states
        .filter(([clientId, state]: [number, any]) => state.user)
        .map(([clientId, state]: [number, any]) => ({
          userId: state.user.userId || String(clientId),
          name: state.user.name || "Anonymous",
          color: state.user.color || "#999",
          avatar: state.user.avatar || null,
          cursor: state.cursor,
        }));

      setActiveUsers(users);
    };

    awareness.on("update", updateUsers);
    updateUsers();

    return () => awareness.off("update", updateUsers);
  }, [awareness]);

  if (activeUsers.length === 0) return null;

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
      }}
    >
      <AvatarGroup stackOrder="last" childWidthHeight="32px" max={10}>
        {activeUsers.map((user) => (
          <Avatar
            key={user.userId}
            sx={{ bgcolor: user.color }}
            src={user.avatar || undefined}
            fullname={user.name}
            tooltip
          ></Avatar>
        ))}
      </AvatarGroup>
      <Box
        component="button"
        type="button"
        onClick={handleCopyLink}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          height: "32px",
          borderRadius: "20px",
          bgcolor: "#FFF",
          border: "1px solid",
          borderColor: theme.palette.neutral["015"],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          overflow: "hidden",
          whiteSpace: "nowrap",
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.14)",
          ml: "-12px",
          zIndex: activeUsers.length + 1,
          position: "relative",
          width: isCopied ? "120px" : isHovered ? "160px" : "32px",
          minWidth: isCopied ? "120px" : isHovered ? "160px" : "32px",
          px: isCopied || isHovered ? 1.5 : 0,
          gap: isCopied || isHovered ? 0.5 : 0,
        }}
        aria-label={isCopied ? "Link copied" : "Invite teammates"}
      >
        {isCopied ? (
          <>
            <CheckCircleOutlinedIcon
              sx={{ fontSize: 18, color: "success.main" }}
            />
            <Typography variant="body-medium" sx={{ color: "text.primary" }}>
              Link copied
            </Typography>
          </>
        ) : (
          <>
            <PlusIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            {isHovered && (
              <Typography variant="body-medium" sx={{ color: "text.primary" }}>
                Invite teammates
              </Typography>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
}
